import { useState } from 'react';
import { HelpCircle, ThumbsUp, MessageSquareReply, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import useWebinarStore from '@/stores/webinarStore';

export default function QnAPanel({ currentUser, isHost }) {
  const { messages, socket, webinarId } = useWebinarStore();
  const [input, setInput] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);

  // Filter only QnA messages
  const questions = messages.filter(m => m.type === 'question');
  const answers = messages.filter(m => m.type === 'answer');

  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim() || !socket) return;

    socket.emit('webinar:chat', {
      content: input.trim(),
      type: replyingTo ? 'answer' : 'question',
      parent_id: replyingTo,
      user_avatar: currentUser?.avatar,
    });
    setInput('');
    setReplyingTo(null);
  };

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      <div className="p-3 border-b border-zinc-800 bg-violet-500/10 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-violet-400 flex items-center gap-2">
          <HelpCircle className="w-4 h-4" /> Questions & Réponses
        </h3>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {questions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-zinc-500">
            <HelpCircle className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-xs">Aucune question posée</p>
          </div>
        ) : (
          <AnimatePresence>
            {questions.map(q => {
              const qAnswers = answers.filter(a => a.parent_id === q.id);
              const isAnswered = qAnswers.length > 0;

              return (
                <motion.div key={q.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                  className={`p-3 rounded-xl border ${isAnswered ? 'bg-zinc-900 border-zinc-800' : 'bg-violet-500/5 border-violet-500/20'}`}>
                  {/* Question */}
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className="text-xs font-semibold text-zinc-300">{q.user_name}</span>
                        <span className="text-[10px] text-zinc-600">
                          {new Date(q.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-200">{q.content}</p>
                    </div>
                  </div>

                  {/* Answers */}
                  {qAnswers.length > 0 && (
                    <div className="mt-3 pl-3 ml-2 border-l-2 border-zinc-800 space-y-2">
                      {qAnswers.map(a => (
                        <div key={a.id} className="bg-zinc-800/50 rounded-lg p-2">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className="text-xs font-semibold text-cyan-400">{a.user_name}</span>
                            <span className="text-[10px] px-1 bg-cyan-500/20 text-cyan-400 rounded">Hôte</span>
                          </div>
                          <p className="text-xs text-zinc-300">{a.content}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-2 flex items-center justify-between pt-2 border-t border-zinc-800/50">
                    <div className="flex items-center gap-2">
                      <button className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-white transition-colors">
                        <ThumbsUp className="w-3 h-3" /> 0
                      </button>
                    </div>
                    {isHost && !isAnswered && (
                      <Button variant="ghost" size="sm" onClick={() => setReplyingTo(q.id)}
                        className="h-6 text-[10px] text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 px-2">
                        <MessageSquareReply className="w-3 h-3 mr-1" /> Répondre
                      </Button>
                    )}
                    {isAnswered && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>

      <div className="p-3 border-t border-zinc-800 bg-zinc-900/50">
        {replyingTo && (
          <div className="mb-2 flex items-center justify-between bg-violet-500/10 text-violet-400 px-2 py-1 rounded text-xs">
            <span>Réponse à une question...</span>
            <button onClick={() => setReplyingTo(null)} className="hover:text-white">✕</button>
          </div>
        )}
        <form onSubmit={handleSend} className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={replyingTo ? "Votre réponse..." : "Poser une question..."}
            className="flex-1 bg-zinc-800 border-zinc-700 text-sm h-9"
          />
          <Button type="submit" disabled={!input.trim()}
            className={`h-9 px-3 ${replyingTo ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-violet-600 hover:bg-violet-700'} text-white`}>
            {replyingTo ? 'Répondre' : 'Poser'}
          </Button>
        </form>
      </div>
    </div>
  );
}
